import type * as SentrySdk from './sentry-sdk';

export type SentryClient = typeof SentrySdk;

type SentryInitializer = (sentry: SentryClient) => void;

let initializeSentry: SentryInitializer | undefined;
let sentryClient: Promise<SentryClient> | undefined;
let isInitialized = false;

// A failed chunk load is retried, but not forever: a page throwing in a loop while the chunk is
// unreachable would otherwise re-request it on every error for the life of the page.
const MAX_LOAD_ATTEMPTS = 3;
let loadAttempts = 0;

const runInitializer = (sentry: SentryClient): SentryClient => {
  if (initializeSentry && !isInitialized) {
    isInitialized = true;
    initializeSentry(sentry);
  }

  return sentry;
};

/**
 * a static `import * as Sentry from '@sentry/nextjs'` in any client module puts the whole
 * browser SDK on the startup path, so it is downloaded, parsed and evaluated before the page is
 * usable. Client code goes through this loader instead, and the SDK exports listed in
 * `sentry-sdk.ts` get their own async chunk.
 */
export const loadSentry = (): Promise<SentryClient> => {
  if (!sentryClient) {
    loadAttempts += 1;
    sentryClient = import('./sentry-sdk').then(runInitializer).catch((error) => {
      // Let the next caller try again. A chunk request that failed once on a flaky network
      // would otherwise disable error reporting for the rest of the page. After the last
      // attempt the rejection stays cached, so later callers fail fast without a network request.
      if (loadAttempts < MAX_LOAD_ATTEMPTS) {
        sentryClient = undefined;
      }
      throw error;
    });
  }

  return sentryClient;
};

/**
 * Registered by an app's client bootstrap before React mounts, so whoever loads the SDK first also
 * initialises it. Apps that keep an eager `Sentry.init()` leave this unset and the loader only
 * imports. On the server the SDK is already initialised by `instrumentation.ts`.
 */
export const setSentryInitializer = (initializer: SentryInitializer) => {
  initializeSentry = initializer;

  // A module that reported an error before this ran already started the load. Initialise that
  // instance too, otherwise the SDK stays loaded but unconfigured and every capture is a no-op.
  sentryClient?.then(runInitializer).catch(() => undefined);
};

export const captureException = (error: unknown, context?: Parameters<SentryClient['captureException']>[1]) => {
  loadSentry()
    .then((sentry) => sentry.captureException(error, context))
    .catch((loadError) => {
      // The reporter itself is unreachable, so there is nowhere to send this but the console.
      // eslint-disable-next-line no-console
      console.error('[sentry] could not load the SDK to report an error', loadError, error);
    });
};
